import { getGridApi, getGridElement, type Column, type GridApi } from 'ag-grid-community';

/**
 * Anchored column-menu opener for custom header UI (e.g. Price Grid group headers).
 *
 * ## Why AG Grid internals are required
 *
 * Public `GridApi.showColumnMenu(colKey)` only opens a leaf menu with AG Grid's default
 * auto-positioning. It cannot:
 * - anchor the popup to an arbitrary button (custom group-header menu controls)
 * - position by mouse for contextmenu on a group header
 * - accept `onClosedCallback` so callers can clear "menu open" CSS/state
 *
 * Native leaf headers use AG Grid's internal `menuSvc.showColumnMenu({ buttonElement |
 * mouseEvent, positionBy, onClosedCallback })`. Group headers have no public equivalent, so
 * we reopen that same service for a representative leaf column.
 *
 * ## Private surfaces used (intentionally, contained here)
 *
 * 1. Element `__AG_*` DOM-data bags — AG Grid stores controller state on header cells under a
 *    property whose name starts with `__AG_` (exact suffix is not part of a public contract).
 * 2. `headerCtrl` entry in that bag — the header cell controller.
 * 3. `headerCtrl.beans.menuSvc` — MenuService used by native header menu buttons (AG Grid 35+).
 * 4. Fallback: `getGridApi(gridRoot).beans.menuSvc` — older / alternate exposure; still private.
 *
 * Prefer (3) over (4). In AG Grid 35, (4) is often unavailable on the grid root.
 *
 * ## Contract / risk
 *
 * - Returning `true` means the internal `menuSvc` path opened the menu (anchoring + close callback).
 * - Returning `false` means we fell back to public `api.showColumnMenu` (or opened nothing). Callers
 *   must not treat that as a tracked open.
 * - AG minor/patch upgrades can rename `__AG_*`, `headerCtrl`, or `menuSvc`. Keep this module as the
 *   single choke point; unit tests cover both the headerCtrl and grid-root beans paths plus fallback.
 */

interface AgGridMenuService {
  showColumnMenu: (
    params: (
      | {
          buttonElement: HTMLElement;
          positionBy: 'button';
        }
      | {
          mouseEvent: MouseEvent | Touch;
          positionBy: 'mouse';
        }
    ) & {
      column: Column;
      onClosedCallback?: () => void;
    }
  ) => void;
}

/** Subset of AG Grid's private bean collection — only `menuSvc` is read. */
interface AgGridInternalBeanCollection {
  menuSvc?: AgGridMenuService;
}

/** Shape of the private object historically hanging off `getGridApi(gridElement)`. */
interface AgGridInternalGridApiBean {
  beans?: AgGridInternalBeanCollection;
}

/** Shape of AG Grid's private header cell controller (`headerCtrl` in the DOM-data bag). */
interface AgGridHeaderCellCtrl {
  beans?: AgGridInternalBeanCollection;
}

/** Key AG Grid uses inside the `__AG_*` DOM-data record for the header cell controller. */
const DOM_DATA_KEY_HEADER_CTRL = 'headerCtrl';

function isDomDataRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isAgGridHeaderCellCtrl(value: unknown): value is AgGridHeaderCellCtrl {
  return isDomDataRecord(value) && isDomDataRecord(value.beans);
}

function isAgGridInternalGridApiBean(value: unknown): value is AgGridInternalGridApiBean {
  return isDomDataRecord(value) && isDomDataRecord(value.beans);
}

/**
 * Reads a value from AG Grid's private per-element DOM-data bag.
 * There is no public API for this; the bag lives on a `__AG_*` own property.
 */
function getDomDataValue(element: Element, key: string): unknown {
  const domDataProperty = Object.getOwnPropertyNames(element).find(property => property.startsWith('__AG_'));
  if (!domDataProperty) {
    return undefined;
  }

  const domData: unknown = Reflect.get(element, domDataProperty);
  return isDomDataRecord(domData) ? domData[key] : undefined;
}

function resolveGridRootFromButton(buttonElement: HTMLElement): Element | undefined {
  let current: Element | null = buttonElement;
  while (current) {
    if (getGridApi(current)) {
      return current;
    }
    current = current.parentElement;
  }

  return undefined;
}

function resolveGridRoot(api: GridApi, buttonElement?: HTMLElement): Element | undefined {
  return (
    getGridElement(api) ??
    (buttonElement ? resolveGridRootFromButton(buttonElement) : undefined) ??
    buttonElement?.closest('.ag-root-wrapper') ??
    buttonElement?.closest('.ag-root') ??
    undefined
  );
}

/**
 * Resolves AG Grid's private MenuService for anchored opens.
 * Prefer leaf `headerCtrl.beans.menuSvc` (AG35); fall back to grid-root `beans.menuSvc` if present.
 */
function getAgGridMenuService(
  api: GridApi,
  sourceElement: HTMLElement,
  menuColumn: Column
): AgGridMenuService | undefined {
  const colId = menuColumn.getColId();
  const gridRoot =
    sourceElement.closest('.ag-root-wrapper') ??
    sourceElement.closest('.ag-root')?.parentElement ??
    sourceElement.closest('[grid-id]')?.parentElement;
  const headerCell = gridRoot?.querySelector(`.ag-header-cell[col-id="${CSS.escape(colId)}"]`);
  if (headerCell) {
    // Primary path (AG Grid 35+): menuSvc is on the leaf header controller, not the grid root.
    const headerCtrl = getDomDataValue(headerCell, DOM_DATA_KEY_HEADER_CTRL);
    if (isAgGridHeaderCellCtrl(headerCtrl) && headerCtrl.beans?.menuSvc) {
      return headerCtrl.beans.menuSvc;
    }
  }

  // Secondary private path: some builds still expose beans.menuSvc via getGridApi(gridRoot).
  const gridElement = resolveGridRoot(api, sourceElement);
  if (!gridElement) {
    return undefined;
  }

  const gridApiEntry = getGridApi(gridElement);
  return isAgGridInternalGridApiBean(gridApiEntry) ? gridApiEntry.beans?.menuSvc : undefined;
}

type AgGridColumnMenuPosition =
  | {
      buttonElement: HTMLElement;
      positionBy: 'button';
    }
  | {
      fallbackElement: HTMLElement;
      mouseEvent: MouseEvent | Touch;
      positionBy: 'mouse';
    };

/**
 * Opens a column menu using the internal MenuService when available so the popup can be anchored
 * and `onClosedCallback` is honored. Falls back to public `GridApi.showColumnMenu` without those
 * guarantees (see module docs).
 *
 * @returns `true` when the anchored menuSvc path opened the menu (close callback is wired).
 *          `false` when falling back to the public API (menu may still open, but without close
 *          tracking) or when nothing could open a menu.
 */
export function showAgGridColumnMenu(
  api: GridApi,
  column: Column,
  menuPosition: AgGridColumnMenuPosition,
  onClosedCallback?: () => void
): boolean {
  const sourceElement =
    menuPosition.positionBy === 'button' ? menuPosition.buttonElement : menuPosition.fallbackElement;
  const menuSvc = getAgGridMenuService(api, sourceElement, column);
  if (menuSvc) {
    if (menuPosition.positionBy === 'button') {
      menuSvc.showColumnMenu({
        column,
        buttonElement: menuPosition.buttonElement,
        positionBy: 'button',
        onClosedCallback,
      });
    } else {
      menuSvc.showColumnMenu({
        column,
        mouseEvent: menuPosition.mouseEvent,
        positionBy: 'mouse',
        onClosedCallback,
      });
    }
    return true;
  }

  // Public API opens a leaf menu without anchoring or onClosedCallback — callers must not treat
  // this as a successfully tracked open (visibility / CSS must not depend on a close callback).
  if (typeof api.showColumnMenu !== 'function') {
    return false;
  }
  api.showColumnMenu(column);
  return false;
}
